import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Upload,
  FolderUp,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  Trash2,
  FileText,
  File,
  Image,
  FileSpreadsheet,
  FileCode,
  Archive
} from 'lucide-react';
import { adminFileService } from '@/services/adminFileService';
import { adminProductsService } from '@/services/adminProducts.service';
import { courseCurriculumService } from '@/services/courseCurriculum.service';
import type { FileLevelType } from '@/types/courseFile.types';
import type { CourseCurriculum } from '@/types/courseCurriculum.types';

interface BulkFileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

interface CourseOption {
  id: string;
  title: string;
  slug: string;
}

interface FileItem {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  progress: number;
  error?: string;
}

// File type icon mapping
const getFileIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';

  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
    return <Image className="w-5 h-5 text-green-400" />;
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return <FileSpreadsheet className="w-5 h-5 text-emerald-400" />;
  }
  if (['py', 'js', 'ts', 'json', 'html', 'css'].includes(ext)) {
    return <FileCode className="w-5 h-5 text-yellow-400" />;
  }
  if (['zip', 'rar'].includes(ext)) {
    return <Archive className="w-5 h-5 text-orange-400" />;
  }
  if (['pdf'].includes(ext)) {
    return <FileText className="w-5 h-5 text-red-400" />;
  }
  if (['doc', 'docx', 'ppt', 'pptx'].includes(ext)) {
    return <FileText className="w-5 h-5 text-blue-400" />;
  }
  return <File className="w-5 h-5 text-gray-400" />;
};

// Generate title from filename
const generateTitleFromFilename = (filename: string): string => {
  const name = filename.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export default function BulkFileUploadModal({
  isOpen,
  onClose,
  onUploadSuccess,
}: BulkFileUploadModalProps) {
  // Course selection state
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [curriculum, setCurriculum] = useState<CourseCurriculum | null>(null);

  // Level selection state
  const [levelType, setLevelType] = useState<FileLevelType>('topic');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  // Files state
  const [files, setFiles] = useState<FileItem[]>([]);

  // UI state
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  // Fetch courses on mount
  useEffect(() => {
    if (isOpen) {
      fetchCourses();
      resetForm();
    }
  }, [isOpen]);

  // Fetch curriculum when course selected
  useEffect(() => {
    if (selectedCourse) {
      fetchCurriculum(selectedCourse);
      setSelectedModule('');
      setSelectedLesson('');
      setSelectedSegment('');
      setSelectedTopic('');
    }
  }, [selectedCourse]);

  // Reset lower selections when parent changes
  useEffect(() => {
    setSelectedLesson('');
    setSelectedSegment('');
    setSelectedTopic('');
  }, [selectedModule]);

  useEffect(() => {
    setSelectedSegment('');
    setSelectedTopic('');
  }, [selectedLesson]);

  useEffect(() => {
    setSelectedTopic('');
  }, [selectedSegment]);

  const fetchCourses = async () => {
    try {
      const response = await adminProductsService.fetchCourses();
      if (response.success && response.data) {
        setCourses(response.data.map((c: any) => ({
          id: c._id || c.id,
          title: c.title,
          slug: c.slug,
        })));
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchCurriculum = async (courseId: string) => {
    try {
      const response = await courseCurriculumService.getCourseCurriculumByCourseId(courseId);
      if (response.success && response.data) {
        setCurriculum(response.data);
      }
    } catch (err) {
      console.error('Error fetching curriculum:', err);
    }
  };

  const resetForm = () => {
    setSelectedCourse('');
    setCurriculum(null);
    setLevelType('topic');
    setSelectedModule('');
    setSelectedLesson('');
    setSelectedSegment('');
    setSelectedTopic('');
    setFiles([]);
    setError(null);
    setUploadComplete(false);
    setIsUploading(false);
  };

  const handleClose = () => {
    if (isUploading) return; // Prevent closing during upload
    resetForm();
    onClose();
  };

  const handleFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      addFiles(Array.from(selectedFiles));
    }
    // Reset input so the same files can be selected again
    e.target.value = '';
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles: FileItem[] = [];
    const errors: string[] = [];

    for (const file of newFiles) {
      const validation = adminFileService.validateFile(file);
      if (validation.valid) {
        // Check for duplicates
        const isDuplicate = files.some(f => f.file.name === file.name && f.file.size === file.size);
        if (!isDuplicate) {
          validFiles.push({
            id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
            file,
            status: 'pending',
            progress: 0,
          });
        }
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError(null);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  }, [files]);

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAllFiles = () => {
    setFiles([]);
    setError(null);
  };

  const getLevelId = (): string => {
    switch (levelType) {
      case 'module': return selectedModule;
      case 'lesson': return selectedLesson;
      case 'segment': return selectedSegment;
      case 'topic': return selectedTopic;
      default: return '';
    }
  };

  const isFormValid = (): boolean => {
    if (!selectedCourse || files.length === 0) return false;
    const levelId = getLevelId();
    return levelId !== '';
  };

  const handleUpload = async () => {
    if (!isFormValid()) return;

    setIsUploading(true);
    setError(null);
    setUploadComplete(false);

    const levelId = getLevelId();
    let successCount = 0;
    let failCount = 0;

    // Process files sequentially to avoid overwhelming the server
    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];

      // Update status to uploading
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id
          ? { ...f, status: 'uploading' as const, progress: 0 }
          : f
      ));

      try {
        await adminFileService.uploadFile(
          {
            courseId: selectedCourse,
            levelType,
            levelId,
            title: generateTitleFromFilename(fileItem.file.name),
            description: '',
            file: fileItem.file,
          },
          (progress) => {
            setFiles(prev => prev.map(f =>
              f.id === fileItem.id
                ? { ...f, progress: progress.progress }
                : f
            ));
          }
        );

        // Mark as complete
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id
            ? { ...f, status: 'complete' as const, progress: 100 }
            : f
        ));
        successCount++;
      } catch (err: any) {
        // Mark as error
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id
            ? {
                ...f,
                status: 'error' as const,
                progress: 0,
                error: err.response?.data?.detail || err.message || 'Upload failed'
              }
            : f
        ));
        failCount++;
      }
    }

    setIsUploading(false);
    setUploadComplete(true);

    // If all succeeded, close modal after delay
    if (failCount === 0) {
      setTimeout(() => {
        onUploadSuccess();
        handleClose();
      }, 1500);
    }
  };

  if (!isOpen) return null;

  const modules = curriculum?.course?.modules || [];
  const selectedModuleData = modules.find(m => m.id === selectedModule);
  const lessons = selectedModuleData?.lessons || [];
  const selectedLessonData = lessons.find(l => l.id === selectedLesson);
  const segments = selectedLessonData?.segments || [];
  const selectedSegmentData = segments.find(s => s.id === selectedSegment);
  const topics = selectedSegmentData?.topics || [];

  // Calculate summary
  const pendingCount = files.filter(f => f.status === 'pending').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const completeCount = files.filter(f => f.status === 'complete').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  // Calculate overall progress
  const overallProgress = files.length > 0
    ? Math.round(files.reduce((sum, f) => sum + f.progress, 0) / files.length)
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-slate-900 border border-white/10 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600/20 rounded-lg">
              <FolderUp className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Bulk Upload Files</h2>
              <p className="text-sm text-gray-400">Upload multiple files to a curriculum location</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Course Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Course</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              disabled={isUploading}
              className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 disabled:opacity-50"
            >
              <option value="">Select a course...</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>{course.title}</option>
              ))}
            </select>
          </div>

          {/* Level Type Selection */}
          {selectedCourse && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Attach to Level</label>
              <div className="grid grid-cols-4 gap-2">
                {(['module', 'lesson', 'segment', 'topic'] as FileLevelType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setLevelType(type)}
                    disabled={isUploading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 ${
                      levelType === type
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                All files will be attached to this location.
              </p>
            </div>
          )}

          {/* Hierarchy Selection */}
          {selectedCourse && curriculum && (
            <div className="space-y-4">
              {/* Module */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Module</label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  disabled={isUploading}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 disabled:opacity-50"
                >
                  <option value="">Select module...</option>
                  {modules.map((m) => (
                    <option key={m.id} value={m.id}>{m.id}. {m.title}</option>
                  ))}
                </select>
              </div>

              {/* Lesson */}
              {selectedModule && levelType !== 'module' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Lesson</label>
                  <select
                    value={selectedLesson}
                    onChange={(e) => setSelectedLesson(e.target.value)}
                    disabled={isUploading}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 disabled:opacity-50"
                  >
                    <option value="">Select lesson...</option>
                    {lessons.map((l) => (
                      <option key={l.id} value={l.id}>{l.id}. {l.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Segment */}
              {selectedLesson && (levelType === 'segment' || levelType === 'topic') && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Segment</label>
                  <select
                    value={selectedSegment}
                    onChange={(e) => setSelectedSegment(e.target.value)}
                    disabled={isUploading}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 disabled:opacity-50"
                  >
                    <option value="">Select segment...</option>
                    {segments.map((s) => (
                      <option key={s.id} value={s.id}>{s.id}. {s.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Topic */}
              {selectedSegment && levelType === 'topic' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Topic</label>
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    disabled={isUploading}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 disabled:opacity-50"
                  >
                    <option value="">Select topic...</option>
                    {topics.map((t) => (
                      <option key={t.id} value={t.id}>{t.id}. {t.content?.substring(0, 50)}...</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Breadcrumb Preview */}
          {getLevelId() && (
            <div className="flex items-center gap-2 text-sm text-gray-400 bg-slate-800/50 rounded-lg p-3">
              <span className="text-purple-400">Path:</span>
              <span>{levelType}</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">{getLevelId()}</span>
            </div>
          )}

          {/* File Upload Area */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">Files</label>
              {files.length > 0 && !isUploading && (
                <button
                  onClick={clearAllFiles}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                files.length > 0 ? 'border-purple-500/50 bg-purple-500/5' : 'border-white/20 hover:border-purple-500/50'
              } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
            >
              <FolderUp className="w-10 h-10 text-gray-500 mx-auto mb-3" />
              <p className="text-gray-400 mb-2">Drag and drop files here, or</p>
              <label className="cursor-pointer">
                <span className="text-purple-400 hover:text-purple-300">browse to select files</span>
                <input
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleFilesSelect}
                  disabled={isUploading}
                  accept={ALLOWED_EXTENSIONS.join(',')}
                />
              </label>
              <p className="text-xs text-gray-500 mt-2">Max 1GB per file. Select multiple files at once.</p>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
                <span>{files.length} file{files.length !== 1 ? 's' : ''} selected</span>
                {isUploading && (
                  <span className="text-purple-400">Overall: {overallProgress}%</span>
                )}
              </div>

              <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
                {files.map((fileItem) => (
                  <div
                    key={fileItem.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      fileItem.status === 'complete'
                        ? 'bg-green-500/10 border-green-500/30'
                        : fileItem.status === 'error'
                        ? 'bg-red-500/10 border-red-500/30'
                        : fileItem.status === 'uploading'
                        ? 'bg-purple-500/10 border-purple-500/30'
                        : 'bg-slate-800/50 border-white/10'
                    }`}
                  >
                    {/* File Icon */}
                    <div className="flex-shrink-0">
                      {getFileIcon(fileItem.file.name)}
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{fileItem.file.name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500">
                          {adminFileService.formatFileSize(fileItem.file.size)}
                        </p>
                        {fileItem.status === 'error' && fileItem.error && (
                          <p className="text-xs text-red-400 truncate">{fileItem.error}</p>
                        )}
                      </div>
                      {fileItem.status === 'uploading' && (
                        <div className="mt-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 transition-all duration-300"
                            style={{ width: `${fileItem.progress}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Status Icon */}
                    <div className="flex-shrink-0">
                      {fileItem.status === 'complete' && (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      )}
                      {fileItem.status === 'error' && (
                        <AlertCircle className="w-5 h-5 text-red-400" />
                      )}
                      {fileItem.status === 'uploading' && (
                        <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                      )}
                      {fileItem.status === 'pending' && !isUploading && (
                        <button
                          onClick={() => removeFile(fileItem.id)}
                          className="p-1 hover:bg-white/10 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Summary */}
          {uploadComplete && (
            <div className={`flex items-center gap-2 p-4 rounded-lg ${
              errorCount > 0
                ? 'bg-yellow-500/10 border border-yellow-500/30'
                : 'bg-green-500/10 border border-green-500/30'
            }`}>
              {errorCount > 0 ? (
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              )}
              <p className={errorCount > 0 ? 'text-yellow-400' : 'text-green-400'}>
                {completeCount} file{completeCount !== 1 ? 's' : ''} uploaded successfully
                {errorCount > 0 && `, ${errorCount} failed`}
              </p>
            </div>
          )}

          {/* Error Messages */}
          {error && (
            <div className="flex items-start gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm whitespace-pre-wrap">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
          <div className="text-sm text-gray-400">
            {isUploading && (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading {uploadingCount > 0 ? `(${completeCount + 1}/${files.length})` : '...'}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="px-6 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg transition-all disabled:opacity-50"
            >
              {uploadComplete ? 'Close' : 'Cancel'}
            </button>
            {!uploadComplete && (
              <button
                onClick={handleUpload}
                disabled={!isFormValid() || isUploading}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {files.length} File{files.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv',
  '.ppt', '.pptx', '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.zip', '.rar', '.py', '.js', '.ts', '.json', '.html', '.css',
  '.txt', '.md'
];
